export default function AboutSection() {
  return (
    <section className="bg-ivory-2 relative overflow-hidden py-24 px-5 lg:px-16" id="about">
      {/* Ghost watermark */}
      <div
        className="absolute bottom-[-60px] right-[-40px] font-syne font-extrabold leading-none pointer-events-none select-none"
        style={{ fontSize: 'clamp(100px,16vw,200px)', color: 'rgba(107,53,168,0.04)', letterSpacing: '-0.04em' }}
        aria-hidden="true"
      >OLÓYE</div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        {/* Left — photo */}
        <div className="relative">
          <div className="relative overflow-hidden rounded-2xl bg-fog" style={{ minHeight: "560px" }}>
            <img
              src="/full-service-event.JPG"
              alt="Olóye Events full service event setup"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
          </div>

          {/* Floating credential card */}
          <div className="absolute -bottom-5 -right-5 bg-white border border-violet/20 rounded-xl shadow-2xl shadow-ink/12 px-6 py-5">
            <p className="font-syne text-[8.5px] font-bold tracking-[0.2em] uppercase text-gold mb-2">Credentials</p>
            <ul className="space-y-1.5">
              {[
                "Woman-Owned Business",
                "Minority-Owned Business",
                "SAM.gov Registered",
                "Fully Insured",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-ink-55 text-xs">
                  <span className="w-1 h-1 rounded-full bg-gold shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right — copy */}
        <div>
          {/* Section label strip */}
          <div className="flex items-center gap-3 mb-5">
            <span className="font-syne text-[10px] font-bold tracking-[0.28em] uppercase text-ink-20">About Us</span>
            <span className="flex-1 h-px bg-ink-10" />
          </div>
          <h2
            className="font-syne font-extrabold text-ink tracking-tight"
            style={{ fontSize: 'clamp(2.2rem,3.5vw,3.5rem)' }}
          >
            We Create Spaces{" "}
            <em className="font-serif italic font-light text-violet-light">People Remember</em>
          </h2>

          <p className="text-[14px] text-ink-55 leading-[1.85] font-light mt-6">
            Olóye Creative Group LLC — doing business as Olóye Events — is a woman and minority-owned event décor company serving New York City and New Jersey.
          </p>

          <p className="text-[14px] text-ink-55 leading-[1.85] font-light mt-4">
            We specialize in balloon installations, custom backdrops, large-format printing, and full event environments. From intimate birthday celebrations to large-scale government and corporate programs, we bring intention and craftsmanship to every setup.
          </p>

          <p className="text-[14px] text-ink-55 leading-[1.85] font-light mt-4">
            As a SAM.gov registered vendor, we're fully equipped to work with government agencies, public institutions, and compliance-sensitive procurement processes — with all documentation available on request.
          </p>

          <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-ink-10">
            {[
              { stat: "100+", label: "Events Delivered" },
              { stat: "2", label: "States Served" },
              { stat: "48h", label: "Quote Turnaround" },
            ].map(({ stat, label }) => (
              <div key={label}>
                <p className="font-syne font-extrabold text-ink tracking-tight text-4xl">{stat}</p>
                <p className="text-ink-35 text-xs mt-1 tracking-wide">{label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            <a
              href="#contact"
              className="bg-violet hover:bg-violet-deep text-white font-syne font-bold text-[10px] tracking-[0.18em] uppercase px-[26px] py-3.5 rounded-sm transition-colors"
            >
              Work With Us
            </a>
            <a
              href="/capability_statement.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-ink-20 text-ink-55 hover:border-violet hover:text-violet font-syne font-bold text-[10px] tracking-[0.18em] uppercase px-[26px] py-3.5 rounded-sm transition-all"
            >
              Capability Statement ↓
            </a>
            <a
              href="https://www.instagram.com/oloye.events?igsh=MTBpZ2VrYTd1ODh5ZQ%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="border border-ink-20 text-ink-35 hover:border-violet hover:text-violet p-3.5 rounded-sm transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4.5"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@olye.events?_r=1&_t=ZT-95HEzsWU9cb"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="border border-ink-20 text-ink-35 hover:border-violet hover:text-violet p-3.5 rounded-sm transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
              </svg>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
